// web-commands.ts — the PER-APP half of the web shape: maps oriora_chores' Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each; list_completions attaches the cheap chore_name/cadence join the
// native SELECT_COMPLETION_VIEW does. The derived signal command (compute_signals) is computed
// on-device in Rust (signals.rs) — on web it returns a valid-but-empty shell for now; a faithful
// web build reimplements that signal logic in JS (the known per-app wrinkle).

import type { CommandHandler, WebStore, Row } from "./web-runtime";
import { computeSignalsWeb } from "./signals-web";
import { browserSearch, browserFetch } from "./web-search-browser";

const MEMBERS = "members";
const CHORES = "chores";
const COMPLETIONS = "completions";
// Household key/value preferences. The WebStore is row-based and keyed on `id`, so a setting
// is stored as { id: <key>, value: <string> } — the browser mirror of the native settings table.
const SETTINGS = "settings";

/**
 * WEB MIRROR of db.rs's `children` → `members` migration (2026-07-29).
 *
 * 🔴 Without this, every existing WEB user is wiped. Their people live in localStorage under the
 * collection key `"children"`, with `child_id` on each chore and completion. Renaming the key in
 * code alone points the app at a collection that does not exist yet — the app opens empty and
 * their history is stranded, which is the exact failure the native migration exists to prevent.
 * The two surfaces share one codebase, so a schema change needs BOTH migrations or it is only
 * half done.
 *
 * Runs once: it is a no-op the moment `members` exists.
 */
function migrateChildrenToMembers(db: WebStore): void {
  const t = db.snapshot();
  if (!t.children || t.members) return;
  const renameFk = (rows: Row[] = []): Row[] =>
    rows.map(({ child_id, ...rest }: Row & { child_id?: unknown }) =>
      child_id === undefined ? rest : { ...rest, member_id: child_id },
    );
  const { children, ...others } = t;
  db.replaceAll({
    ...others,
    members: children,
    chores: renameFk(t.chores),
    completions: renameFk(t.completions),
  });
}

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  migrateChildrenToMembers(db);

  const memberName = (id: string): string => (db.get(MEMBERS, id) as { name?: string } | null)?.name ?? "";

  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Settings (household key/value) ──────────────────────────────────────
    // Returns null when unset, matching the native command — the caller supplies the default.
    get_setting: ({ key }) => {
      const row = db.get(SETTINGS, String(key));
      return row ? String((row as { value?: unknown }).value ?? "") : null;
    },
    set_setting: ({ key, value }) => {
      const k = String(key);
      if (db.get(SETTINGS, k)) db.update(SETTINGS, k, { value: String(value ?? "") });
      else db.insert(SETTINGS, { id: k, value: String(value ?? "") });
      return null;
    },

    // ── Members ─────────────────────────────────────────────────────────────
    list_members: () =>
      db.all(MEMBERS).sort((a, b) =>
        String((a as { name?: string }).name ?? "").localeCompare(String((b as { name?: string }).name ?? "")),
      ),
    get_member: ({ id }) => db.get(MEMBERS, String(id)),
    create_member: ({ input }) => {
      const i = input as Record<string, unknown>;
      const name = String(i.name ?? "").trim();
      const now = nowIso();
      const row: Row = { id: uuid(), name, created_at: now, updated_at: now };
      return db.insert(MEMBERS, row);
    },
    update_member: ({ id, patch }) => {
      const p = patch as Record<string, unknown>;
      const update: Record<string, unknown> = { updated_at: nowIso() };
      if (typeof p.name === "string" && p.name.trim()) update.name = p.name.trim();
      db.update(MEMBERS, String(id), update);
      return null;
    },
    delete_member: ({ id }) => {
      // ON DELETE CASCADE in db.rs — mirror by removing this member's chores + completions too.
      const cid = String(id);
      for (const c of db.all(COMPLETIONS).filter((c) => (c as { member_id?: string }).member_id === cid)) {
        db.remove(COMPLETIONS, c.id);
      }
      for (const ch of db.all(CHORES).filter((ch) => (ch as { member_id?: string }).member_id === cid)) {
        db.remove(CHORES, ch.id);
      }
      db.remove(MEMBERS, cid);
      return null;
    },

    // ── Chores ───────────────────────────────────────────────────────────────
    list_chores: ({ memberId, activeOnly }) => {
      let rows = db.all(CHORES);
      if (memberId) rows = rows.filter((r) => (r as { member_id?: string }).member_id === String(memberId));
      if (activeOnly) rows = rows.filter((r) => Number((r as { active?: number }).active) === 1);
      rows.sort((a, b) => {
        const aa = Number((a as { active?: number }).active ?? 0);
        const ab = Number((b as { active?: number }).active ?? 0);
        if (aa !== ab) return ab - aa; // active DESC
        return String((a as { name?: string }).name ?? "").localeCompare(String((b as { name?: string }).name ?? ""));
      });
      return rows;
    },
    get_chore: ({ id }) => db.get(CHORES, String(id)),
    create_chore: ({ input }) => {
      const i = input as Record<string, unknown>;
      const now = nowIso();
      const points = Math.max(0, Number(i.points ?? 1));
      const cadence = String(i.cadence ?? "").trim() || "once";
      const row: Row = {
        id: uuid(),
        member_id: String(i.member_id ?? ""),
        name: String(i.name ?? "").trim(),
        points,
        cadence,
        active: 1,
        rotation: String(i.rotation ?? "").trim(),
        // Defaulted for the same reason as rotation: native SQLite defaults the column to '',
        // and newRow-style spreads drop undefined, which renders as `undefined` on web.
        due_on: String(i.due_on ?? "").trim(),
        created_at: now,
        updated_at: now,
      };
      return db.insert(CHORES, row);
    },
    update_chore: ({ id, patch }) => {
      const p = patch as Record<string, unknown>;
      const update: Record<string, unknown> = { updated_at: nowIso() };
      if (typeof p.name === "string" && p.name.trim()) update.name = p.name.trim();
      if (typeof p.points === "number") update.points = Math.max(0, p.points);
      if (typeof p.cadence === "string" && p.cadence.trim()) update.cadence = p.cadence.trim();
      if (typeof p.active === "number") update.active = p.active !== 0 ? 1 : 0;
      if (typeof p.rotation === "string") update.rotation = p.rotation.trim();
      if (typeof p.member_id === "string" && p.member_id) update.member_id = p.member_id;
      if (typeof p.due_on === "string") update.due_on = p.due_on.trim();
      db.update(CHORES, String(id), update);
      return null;
    },
    delete_chore: ({ id }) => {
      // ON DELETE CASCADE in db.rs — mirror by removing this chore's completions too.
      const chid = String(id);
      for (const c of db.all(COMPLETIONS).filter((c) => (c as { chore_id?: string }).chore_id === chid)) {
        db.remove(COMPLETIONS, c.id);
      }
      db.remove(CHORES, chid);
      return null;
    },

    // ── Completions ──────────────────────────────────────────────────────────
    list_completions: ({ memberId }) => {
      let rows = db.all(COMPLETIONS);
      if (memberId) rows = rows.filter((r) => (r as { member_id?: string }).member_id === String(memberId));
      const joined = rows.map((r) => {
        const chore = db.get(CHORES, String((r as { chore_id?: string }).chore_id ?? "")) as
          | { name?: string; cadence?: string }
          | null;
        return { ...r, chore_name: chore?.name ?? "(removed chore)", cadence: chore?.cadence ?? "" };
      });
      joined.sort((a, b) => {
        const ad = String((a as { done_on?: string }).done_on ?? "");
        const bd = String((b as { done_on?: string }).done_on ?? "");
        if (ad !== bd) return bd.localeCompare(ad); // done_on DESC
        const ac = String((a as { created_at?: string }).created_at ?? "");
        const bc = String((b as { created_at?: string }).created_at ?? "");
        return bc.localeCompare(ac); // created_at DESC
      });
      return joined;
    },
    get_completion: ({ id }) => db.get(COMPLETIONS, String(id)),
    create_completion: ({ input }) => {
      const i = input as Record<string, unknown>;
      const choreId = String(i.chore_id ?? "");
      const chore = db.get(CHORES, choreId) as { points?: number } | null;
      const done_on = typeof i.done_on === "string" && i.done_on ? i.done_on : todayLocal();
      const points = Math.max(0, Number(i.points ?? chore?.points ?? 0));
      const note = String(i.note ?? "").trim();
      const photo = typeof i.photo === "string" ? i.photo : "";
      const row: Row = {
        id: uuid(),
        member_id: String(i.member_id ?? ""),
        chore_id: choreId,
        done_on,
        points,
        note,
        photo,
        created_at: nowIso(),
      };
      return db.insert(COMPLETIONS, row);
    },
    update_completion: ({ id, patch }) => {
      const p = patch as Record<string, unknown>;
      const update: Record<string, unknown> = {};
      if (typeof p.done_on === "string" && p.done_on) update.done_on = p.done_on;
      if (typeof p.points === "number") update.points = Math.max(0, p.points);
      if (typeof p.note === "string") update.note = p.note.trim();
      db.update(COMPLETIONS, String(id), update);
      return null;
    },
    delete_completion: ({ id }) => {
      db.remove(COMPLETIONS, String(id));
      return null;
    },

    // ── Derived signals — the SAME figures the Rust computes (signals.rs) ──────────
    // Was a zero stub, so the Weekly Recap could never work on web AND the billed AI call was
    // handed an empty object. Ported in signals-web.ts; the RUST WINS on any drift.
    compute_signals: ({ memberId, today, periodDays }) => {
      const id = String(memberId ?? "");
      const m = db.get(MEMBERS, id) as { id: string; name: string } | null;
      if (!m) return null;
      return computeSignalsWeb(
        { id: m.id, name: m.name },
        db.all(CHORES) as unknown as Parameters<typeof computeSignalsWeb>[1],
        db.all(COMPLETIONS) as unknown as Parameters<typeof computeSignalsWeb>[2],
        String(today ?? todayLocal()),
        Number(periodDays ?? 7),
      );
    },
  };
}
